import { Box, TextField, Grid2, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormik } from "formik";

// Multi-lingual support
import { t } from "i18next";

// Communication details types
import { countryCodes } from "../../../../../components/shared/commonTypes/commonTypes";

// Hooks
import { useMobileInput } from "../../../../../hooks/useMobileNumberInput/useMobileNumberInput";
import useTrimContent from "../../../../../hooks/useTrimContent/useTrimContent";

// Validation schema
import { getCommunicationDetailsValidationSchema } from "./CommunicationDetailsValidationSchema";

const CommunicationDetails = ({
  setValidateStep,
  onSubmit,
  onFieldChange,
  farmerData,
  apiErrorCode,
}: any) => {
  // Validate mobile input field with custom hook
  const { handleInputChange } = useMobileInput();

  // Use the useFormik hook to manage the form
  const formik = useFormik({
    initialValues: {
      countryCode: "+66",
      mobileNumber: "",
      address: "",
      province: "",
      district: "",
      subdistrict: "",
      postalcode: "",
      village: "",
    },
    validationSchema: getCommunicationDetailsValidationSchema(),
    onSubmit: (values) => {
      const farmerData: any = { ...values };
      onSubmit(farmerData);
    },
  });

  // Handle trim on blur
  const handleTrimOnBlur = useTrimContent(formik);

  useEffect(() => {
    if (setValidateStep) {
      setValidateStep(() => formik.submitForm);
    }
  }, [formik.submitForm, setValidateStep]);

  // Get meta data and set existing data
  useEffect(() => {
    // Edit case handling
    if (farmerData && farmerData) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        countryCode: farmerData?.countryCode || "+66",
        mobileNumber: farmerData?.mobileNumber || "",
        address: farmerData?.address || "",
        province: farmerData?.province || "",
        district: farmerData?.district || "",
        subdistrict: farmerData?.subdistrict || "",
        postalcode: farmerData?.postalcode || "",
        village: farmerData?.village || "",
      }));
    }
  }, []);

  useEffect(() => {
    console.log(apiErrorCode);
  }, [apiErrorCode]);

  return (
    <Box sx={{ paddingLeft: 2 }}>
      {/* Form Container */}
      <Box
        sx={{
          backgroundColor: "farm.contrastText",
          maxWidth: "auto",
          mx: "auto",
          height: "var(--AgentWebHeight-farmerstepper)",
          overflowY: "auto",
        }}
      >
        <Grid2 container spacing={2} mt={3}>
          {/* Contact Number */}
          <Grid2 size={3}>
            <TextField
              select
              fullWidth
              size="small"
              id="countryCode"
              variant="filled"
              label={`${t("addUpdateFarmer.countryCode")}*`}
              {...formik.getFieldProps("countryCode")}
            >
              {countryCodes.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.code}
                </MenuItem>
              ))}
            </TextField>
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={`${t("addUpdateFarmer.contactNo")}*`}
              variant="filled"
              name="mobileNumber"
              value={formik.values.mobileNumber}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => {
                // Get the new value from the input field
                const newValue = e.target.value;

                // Reset the field error
                formik.handleChange(e);
                onFieldChange();

                // Handle the input change
                handleInputChange(
                  newValue,
                  formik.setFieldValue,
                  "mobileNumber",
                  formik.values.countryCode,
                );
              }}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.mobileNumber &&
                  Boolean(formik.errors.mobileNumber)) ||
                apiErrorCode === "E_FARMER_DUPLICATE_PHONENUMBER"
              }
              helperText={
                formik.touched.mobileNumber && formik.errors.mobileNumber
              }
            />
            {/* API Error Message */}
            {apiErrorCode &&
              apiErrorCode === "E_FARMER_DUPLICATE_PHONENUMBER" && (
                <Box
                  sx={{
                    color: "agricolors.error",
                    fontSize: "12px",
                    marginLeft: "14px",
                  }}
                >
                  {t("addUpdateFarmer.duplicateMobileNumber")}
                </Box>
              )}
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} mt={3}>
          {/* Address */}
          <Grid2 size={6}>
            <TextField
              fullWidth
              size="small"
              label={<span>{`${t("addUpdateFarmer.address")}`}</span>}
              variant="filled"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} mt={3}>
          {/* District & Province */}
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={<span>{`${t("addUpdateFarmer.province")}`}</span>}
              variant="filled"
              name="province"
              value={formik.values.province}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={formik.touched.province && Boolean(formik.errors.province)}
              helperText={formik.touched.province && formik.errors.province}
            />
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={<span>{`${t("addUpdateFarmer.district")}`}</span>}
              variant="filled"
              name="district"
              value={formik.values.district}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={formik.touched.district && Boolean(formik.errors.district)}
              helperText={formik.touched.district && formik.errors.district}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} mt={3}>
          {/* Sub-District & Village */}
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={<span>{`${t("addUpdateFarmer.subDistrict")}`}</span>}
              variant="filled"
              name="subdistrict"
              value={formik.values.subdistrict}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={
                formik.touched.subdistrict && Boolean(formik.errors.subdistrict)
              }
              helperText={
                formik.touched.subdistrict && formik.errors.subdistrict
              }
            />
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={<span>{`${t("addUpdateFarmer.postalCode")}`}</span>}
              variant="filled"
              name="postalcode"
              value={formik.values.postalcode}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={
                formik.touched.postalcode && Boolean(formik.errors.postalcode)
              }
              helperText={formik.touched.postalcode && formik.errors.postalcode}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} mt={3}>
          {/* Postal Code */}
          <Grid2 size={3}>
            <TextField
              fullWidth
              size="small"
              label={<span>{`${t("addUpdateFarmer.village")}`}</span>}
              variant="filled"
              name="village"
              value={formik.values.village}
              onChange={formik.handleChange}
              onBlur={handleTrimOnBlur}
              error={formik.touched.village && Boolean(formik.errors.village)}
              helperText={formik.touched.village && formik.errors.village}
            />
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default CommunicationDetails;
